'use client'

import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useMemo } from 'react'
import { create } from 'zustand'

// router.push only updates the URL (and useSearchParams) once the server render
// for the new params is done, which can take a while on availability pages.
// Until then we keep the queries we pushed here, so the next update builds on
// them instead of the stale URL, and readers see the intended values right away.
type PendingParams = {
  pathname: string
  pushed: string[]
}

const usePendingParamsStore = create<{ pending: PendingParams | null }>(() => ({
  pending: null,
}))

export function useBookingParams() {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const pending = usePendingParamsStore((s) => s.pending)

  const committed = searchParams.toString()

  // Drop the pending queries once the last push has landed, or when some other
  // navigation (a link, back button, another page) took over
  useEffect(() => {
    const { pending } = usePendingParamsStore.getState()
    if (!pending) return

    const isLatest = pending.pushed.at(-1) === committed
    const isOurs = pending.pushed.includes(committed)

    if (pending.pathname !== pathname || isLatest || !isOurs) {
      usePendingParamsStore.setState({ pending: null })
    }
  }, [pathname, committed])

  const latest =
    pending && pending.pathname === pathname ? (pending.pushed.at(-1) ?? committed) : committed

  const params = useMemo(() => new URLSearchParams(latest), [latest])

  const updateParams = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      const { pending } = usePendingParamsStore.getState()
      const current = pending && pending.pathname === pathname ? pending : null

      const next = new URLSearchParams(current?.pushed.at(-1) ?? window.location.search)
      mutate(next)
      const query = next.toString()

      usePendingParamsStore.setState({
        pending: {
          pathname,
          // Keep the committed query so an intermediate commit isn't seen as foreign
          pushed: [...(current?.pushed ?? [committed]), query],
        },
      })

      router.push(`?${query}`, { scroll: false })
    },
    [router, pathname, committed],
  )

  return { params, updateParams }
}
